/**
 * Traveler types and interfaces for the unified person management system
 */

// Person types enum
export const PersonTypes = {
  DRIVER: 'Driver',
  STAFF: 'Staff', 
  COACH: 'Coach',
  PLAYER: 'Player'
} as const;

export type PersonType = typeof PersonTypes[keyof typeof PersonTypes];

// License classes for drivers
export const LicenseClasses = {
  B: 'B',
  C: 'C',
  C1: 'C1',
  D: 'D',
  D1: 'D1',
  BE: 'BE',
  CE: 'CE',
  C1E: 'C1E',
  DE: 'DE',
  D1E: 'D1E'
} as const;

export type LicenseClass = typeof LicenseClasses[keyof typeof LicenseClasses];

// Main Traveler interface
export interface Traveler {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  personType: PersonType;
  personTypeDisplay: string;
  licenseClasses: LicenseClass[];
  licenseExpiry?: string;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  
  // Computed properties
  isDriver: boolean;
  isLicenseExpired?: boolean;
  licenseClassesList: LicenseClass[];
}

// Request/Response DTOs
export interface CreateTravelerRequest {
  firstName: string;
  lastName: string;
  personType: PersonType;
  licenseClasses?: LicenseClass[];
  licenseExpiry?: string;
  notes?: string;
}

export interface UpdateTravelerRequest {
  firstName?: string;
  lastName?: string;
  personType?: PersonType;
  licenseClasses?: LicenseClass[];
  licenseExpiry?: string;
  notes?: string;
  isActive?: boolean;
}

// Summary/Statistics interface
export interface TravelerSummary {
  totalTravelers: number;
  activeTravelers: number;
  drivers: number;
  staff: number;
  coaches: number;
  players: number;
  driversWithValidLicense: number;
  driversWithExpiredLicense: number;
}

// Person type info for UI display
export interface PersonTypeInfo {
  value: PersonType;
  label: string;
  icon: string;
  description: string;
}

// License class info for UI display
export interface LicenseClassInfo {
  value: LicenseClass;
  label: string;
  description: string;
  vehicleTypes: string[];
}

// Utility functions
export const getPersonTypeInfo = (personType: PersonType): PersonTypeInfo => {
  const personTypeMap: Record<PersonType, PersonTypeInfo> = {
    [PersonTypes.DRIVER]: {
      value: PersonTypes.DRIVER,
      label: 'Fahrer',
      icon: '',
      description: 'Fahrzeugführer für Transportdienstleistungen'
    },
    [PersonTypes.STAFF]: {
      value: PersonTypes.STAFF,
      label: 'Staff',
      icon: '',
      description: 'Mitarbeiter (Physiotherapeut, Manager, etc.)'
    },
    [PersonTypes.COACH]: {
      value: PersonTypes.COACH,
      label: 'Trainerstab',
      icon: '',
      description: 'Trainer, Co-Trainer, Assistenten'
    },
    [PersonTypes.PLAYER]: {
      value: PersonTypes.PLAYER,
      label: 'Spieler',
      icon: '',
      description: 'Aktive Spieler des Teams'
    }
  };
  
  return personTypeMap[personType];
};

export const getLicenseClassInfo = (licenseClass: LicenseClass): LicenseClassInfo => {
  const licenseClassMap: Record<LicenseClass, LicenseClassInfo> = {
    [LicenseClasses.B]: {
      value: LicenseClasses.B,
      label: 'Klasse B',
      description: 'PKW bis 3,5t',
      vehicleTypes: ['car']
    },
    [LicenseClasses.C]: {
      value: LicenseClasses.C,
      label: 'Klasse C',
      description: 'LKW über 3,5t',
      vehicleTypes: ['truck']
    },
    [LicenseClasses.C1]: {
      value: LicenseClasses.C1,
      label: 'Klasse C1',
      description: 'LKW 3,5t - 7,5t',
      vehicleTypes: ['van', 'truck']
    },
    [LicenseClasses.D]: {
      value: LicenseClasses.D,
      label: 'Klasse D',
      description: 'Bus über 8 Personen',
      vehicleTypes: ['bus']
    },
    [LicenseClasses.D1]: {
      value: LicenseClasses.D1,
      label: 'Klasse D1',
      description: 'Kleinbus 9-16 Personen',
      vehicleTypes: ['bus']
    },
    [LicenseClasses.BE]: {
      value: LicenseClasses.BE,
      label: 'Klasse BE',
      description: 'PKW mit Anhänger',
      vehicleTypes: ['car']
    },
    [LicenseClasses.CE]: {
      value: LicenseClasses.CE,
      label: 'Klasse CE',
      description: 'LKW mit Anhänger',
      vehicleTypes: ['truck']
    },
    [LicenseClasses.C1E]: {
      value: LicenseClasses.C1E,
      label: 'Klasse C1E',
      description: 'LKW 3,5t-7,5t mit Anhänger',
      vehicleTypes: ['van', 'truck']
    },
    [LicenseClasses.DE]: {
      value: LicenseClasses.DE,
      label: 'Klasse DE',
      description: 'Bus mit Anhänger',
      vehicleTypes: ['bus']
    },
    [LicenseClasses.D1E]: {
      value: LicenseClasses.D1E,
      label: 'Klasse D1E',
      description: 'Kleinbus mit Anhänger',
      vehicleTypes: ['bus']
    }
  };
  
  return licenseClassMap[licenseClass];
};

// Helper functions
export const isLicenseExpired = (licenseExpiry?: string): boolean => {
  if (!licenseExpiry) return false;
  return new Date(licenseExpiry) < new Date();
};

export const isLicenseExpiringSoon = (licenseExpiry?: string, daysThreshold: number = 30): boolean => {
  if (!licenseExpiry) return false;
  const expiryDate = new Date(licenseExpiry);
  const thresholdDate = new Date();
  thresholdDate.setDate(thresholdDate.getDate() + daysThreshold);
  return expiryDate <= thresholdDate && expiryDate >= new Date();
};

export const formatLicenseClasses = (licenseClasses: LicenseClass[]): string => {
  if (!licenseClasses || licenseClasses.length === 0) return '-';
  return licenseClasses.join(', ');
};

export const canDriveVehicleType = (traveler: Traveler, vehicleType: string): boolean => {
  if (!traveler.isDriver || !traveler.licenseClasses) return false;
  
  return traveler.licenseClasses.some(licenseClass => {
    const info = getLicenseClassInfo(licenseClass);
    return info.vehicleTypes.includes(vehicleType);
  });
};
